/**
 * Mock data for Practice Enrollments
 *
 * PHI-safe: provider/payer names and operational status only; no patient data.
 * Assumption: real implementation will use FHIR or backend API (e.g. Practitioner, Organization, Task).
 */

import type { BillingThroughRelationship, Enrollment } from '../types';

/** Providers available for new enrollment (e.g. from Practitioner search) */
export const mockProviders = [
  { id: 'p1', name: 'Dr. Jane Smith' },
  { id: 'p2', name: 'Dr. John Davis' },
  { id: 'p3', name: 'Sarah Chen, LCSW' },
  { id: 'p4', name: 'Michael Torres, LMFT' },
];

/** Payers available for new enrollment (e.g. from Organization/Payer list) */
export const mockPayers = [
  { id: 'pay1', name: 'Aetna' },
  { id: 'pay2', name: 'UnitedHealthcare' },
  { id: 'pay3', name: 'Kaiser Permanente' },
  { id: 'pay4', name: 'Cigna' },
  { id: 'pay5', name: 'Blue Cross' },
];

/** Billing entities for "billing through" enrollment type */
export const mockBillingEntities = [
  { id: 'org1', name: 'Meridian Counseling Group' },
];

export const mockEnrollments: Enrollment[] = [
  {
    id: 'e1',
    providerId: 'p1',
    providerName: 'Dr. Jane Smith',
    payerId: 'pay1',
    payerName: 'Aetna',
    enrollmentType: 'individual',
    status: 'approved',
    billingReady: true,
    effectiveDate: '2024-06-01',
    updatedAt: '2025-01-15T10:00:00Z',
    updatedBy: 'credentialing@practice.org',
  },
  {
    id: 'e2',
    providerId: 'p2',
    providerName: 'Dr. John Davis',
    payerId: 'pay1',
    payerName: 'Aetna',
    enrollmentType: 'individual',
    status: 'pending-payer-response',
    billingReady: false,
    currentStage: 'Pending payer response',
    daysInStage: 14,
    nextAction: 'Follow up with payer if no response by day 21',
    submittedAt: '2025-01-02T09:00:00Z',
    submittedBy: 'credentialing@practice.org',
    updatedAt: '2025-01-16T14:00:00Z',
    updatedBy: 'credentialing@practice.org',
  },
  {
    id: 'e3',
    providerId: 'p2',
    providerName: 'Dr. John Davis',
    payerId: 'pay2',
    payerName: 'UnitedHealthcare',
    enrollmentType: 'billing-through',
    status: 'in-progress',
    billingReady: false,
    currentStage: 'Primary Source Verification',
    daysInStage: 8,
    nextAction: 'Upload signed W-9',
    blockers: ['Missing W-9'],
    billingThroughEntityId: 'org1',
    billingThroughEntityName: 'Meridian Counseling Group',
    updatedAt: '2025-01-20T11:00:00Z',
    updatedBy: 'credentialing@practice.org',
  },
  {
    id: 'e4',
    providerId: 'p3',
    providerName: 'Sarah Chen, LCSW',
    payerId: 'pay2',
    payerName: 'UnitedHealthcare',
    enrollmentType: 'individual',
    status: 'not-started',
    billingReady: false,
    nextAction: 'Collect required documents and initiate application',
    updatedAt: '2025-01-10T08:00:00Z',
  },
  {
    id: 'e5',
    providerId: 'p1',
    providerName: 'Dr. Jane Smith',
    payerId: 'pay2',
    payerName: 'UnitedHealthcare',
    enrollmentType: 'individual',
    status: 'submitted',
    billingReady: false,
    currentStage: 'Application submitted',
    daysInStage: 3,
    nextAction: 'Wait for payer confirmation',
    submittedAt: '2025-01-25T16:00:00Z',
    submittedBy: 'credentialing@practice.org',
    updatedAt: '2025-01-25T16:00:00Z',
    updatedBy: 'credentialing@practice.org',
  },
  {
    id: 'e6',
    providerId: 'p3',
    providerName: 'Sarah Chen, LCSW',
    payerId: 'pay3',
    payerName: 'Kaiser Permanente',
    enrollmentType: 'individual',
    status: 'denied',
    billingReady: false,
    currentStage: 'Denied',
    nextAction: 'Review denial reason and reapply or appeal',
    blockers: ['Contract capacity limit'],
    updatedAt: '2025-01-18T09:00:00Z',
    updatedBy: 'credentialing@practice.org',
  },
];

export const mockBillingThroughRelationships: BillingThroughRelationship[] = [
  {
    id: 'bt1',
    providerId: 'p2',
    providerName: 'Dr. John Davis',
    billingEntityId: 'org1',
    billingEntityName: 'Meridian Counseling Group',
    effectiveStart: '2024-09-01',
    payersImpacted: ['UnitedHealthcare', 'Cigna'],
    warnings: ['UnitedHealthcare enrollment still in progress'],
  },
  {
    id: 'bt2',
    providerId: 'p4',
    providerName: 'Michael Torres, LMFT',
    billingEntityId: 'org1',
    billingEntityName: 'Meridian Counseling Group',
    effectiveStart: '2024-03-15',
    effectiveEnd: '2025-06-30',
    payersImpacted: ['Aetna', 'Blue Cross'],
    warnings: ['Relationship expires 2025-06-30'],
  },
];
